import * as THREE from 'three';
import { showHideButtonPlay } from '../camera-auto-tour/cameraTour';
import { Buildings, isMobile, projectMode, settingsData, threeJsVariables } from '../../settings';
import { ViewMode } from '../../common';
import {findSelectionAndCrmData, flatClick, removeSelectionBoxesLockColor, userPrevent} from '../../functions';
import { closePoiInfoWindowCardInOrbit, poiToggle } from '../poi/poiCss';
import { getHighlights, getSelectionsByName } from '../threejs/autoSelectionBoxes';
import { autoZoomIsActive, fitModelInViewport, getZoomParams } from '../controls/zoomControl';
import { Vector3 } from 'three';
import {
    autoRotateTimerAction,
    getOrbitControlsDistanceAndPan,
    getPolarAngleFromViewMode,
    getWorldData, hideOrbitToMapButton,
    resetPanLimits,
    setMinAndMaxControlsDistance,
    setOrbitControls,
    setPanLimits,
    setPolarAngle,
    setSmoothDistance,
} from '../controls/orbitControls';
import { animateCamera } from '../threejs/animation';
import TWEEN from '../../libs/tween.umd';
import { toggleStreetNames } from '../street-names/addStreetNames';
import { hideRightNavBar, moveElements, showRightNavBar } from '../navigation/right-nav-bar';
import { additionalMenuToggle, mobileBurgerStyleToggle, setMenuActive } from '../menu/menu';
import {
    closeUnitCard,
    hideBuildings, hideBuildingSwitcher,
    showBuildings,
    showBuildingSwitcher,
    showAllBuildings
} from '../building-switch/building_switch';
import {
    cylinderFloorNumbers,
    removeCylinderFloorNumbers, rotateCylinderFloorNumbers
} from '../cylinderFloorNumbers/cylinderFloorNumbers';
import {
    hideCylindersGrid,
    showCylinderGrid,
} from '../cylinderGrid/cylinderGrid';
import { buildingHoverOut } from '../threejs/raycast';
import {
    buildingsLabelHideWhenAnimationStart,
    showBuildingLabels,
} from '../building-switch/buildingLabels';
import { environmentHide, environmentToggle } from '../threejs/environment';
import { backgroundHide, backgroundShow } from '../threejs/sceneBackground';
import {
    changeSelectionBoxesStatusColor,
    markSelectedApartments,
    openSearchHandler,
    removeSearchContainer,
    updateSearchResults
} from '../search/search';
import { setActiveMultiSearchButton } from '../search/multipleSearch';
import { changeLightEnvironment, updateShadowMap } from '../threejs/lights';
import { canvasResize } from '../../app';
import { hideTooltip } from '../flatTooltip/flatTooltip';
import { isDesktopMode } from '../language/langSwitcher';
import { Amenities } from '../amenities/Amenities';
import { analytics } from '../analytics/analytics';
import { hideSceneObjects, showSceneObjects } from '../points/points';
import { Weather } from '../weather/Weather';


export class Cylinder {
    constructor () {
        this.minAngle = getPolarAngleFromViewMode(ViewMode.Cylinder).minAngle;
        this.minDistance = null;
        this.maxDistance = null;
        this.selectionBoxes = null;
        this.switchBuildingDuration = 1000;
        this.cylinderEnterDuration = 1500;
    }

    async enter (building) {
        if (config.features?.blockCylinderMode) return;
        if (userPrevent()) return;

        if (building?.isMesh) {

            if (isDesktopMode()){

                if (settingsData.isTouchEvent && !settingsData.doubleClick){

                    return;
                }

            }

            // building = buildings_info.find(tower => tower.name === building.parent.name);
            building = Buildings.getBuilding(building.parent.name);

            if (!Object.keys(building.info).length) return;

        }

        if (!building){
            return;
        }

        userPrevent(true);

        hideOrbitToMapButton();

        if (Weather.isActive()) {
            Weather.toggle('disable');
        }

        settingsData.activeModeSwitching = true;

        Buildings.activateBuilding(building.name);

        if (projectMode.LiteMode.isActive()) {

            projectMode.LiteMode.hideLiteMode();

            if (projectMode.LiteMode.modeIs('picture')) {
                userPrevent(false);
                settingsData.activeViewMode = 'cylinder';
                projectMode.LiteMode.moveCameraToBuilding(building.name);

                await openSearchHandler();
                showRightNavBar();
                setMenuActive('search');
                analytics('page_entrance','search');

                projectMode.Pagination.paginate({source: 'menuHandler', note: 'cylinderMode', menuItem: 'search'});
                settingsData.activeModeSwitching = false;

                return;
            }

        }

        buildingHoverOut();
        autoRotateTimerAction('cancel');

        showHideButtonPlay('hide');
        removeSelectionBoxesLockColor();

        if (isMobile) {
            closePoiInfoWindowCardInOrbit(true);
        } else {
            closePoiInfoWindowCardInOrbit(false);
        }

        const currentBuilding = building.selectionsGroup;
        const buildingBox = new THREE.Box3().setFromObject(currentBuilding);
        await fitModelInViewport(false, currentBuilding);

        this.updateVariables();

        const buildingBoxCenter = buildingBox.getCenter(new Vector3());
        const buildingCenterPoint = currentBuilding.getWorldPosition(new Vector3()).setY(buildingBoxCenter.y);

        const {cameraVector, orbitControlsVector} = getWorldData();
        let length;

        this.selectionBoxes = currentBuilding.parent;
        threeJsVariables.scene.add(this.selectionBoxes);

        if (autoZoomIsActive()) {
            const {foreshortening} = getZoomParams();
            length = foreshortening;
        } else {
            length = (this.maxDistance + this.minDistance) / 2;
        }

        const position = new Vector3().subVectors(cameraVector, buildingCenterPoint).normalize().setLength(length).add(buildingCenterPoint);
        buildingCenterPoint.y = buildingCenterPoint.y / 1.2;

        settingsData.activeViewMode = 'cylinder';
        additionalMenuToggle();
        
        await animateCamera(
            cameraVector,
            position,
            orbitControlsVector,
            buildingCenterPoint,
            undefined,
            this.minDistance,
            this.minAngle,
            config.threejs.orbitControls.polarCameraCorrection,
            this.cylinderEnterDuration,
            TWEEN.Easing.Sinusoidal.InOut,
            () => {
                buildingsLabelHideWhenAnimationStart();
                setSmoothDistance();
                backgroundHide();
                toggleStreetNames(false);
                environmentHide();
                showCylinderGrid(building);
                poiToggle(false);
                resetPanLimits();
                // hideBuildings(selectedBuildingIndex);
                // showBuildings(selectedBuildingIndex);
                hideBuildings();
                // showBuildings(building);
                hideSceneObjects();
            },
        );

        setOrbitControls(ViewMode.Cylinder);
        setPolarAngle(ViewMode.Cylinder);
        setMinAndMaxControlsDistance(ViewMode.Cylinder);
        setPanLimits(ViewMode.Cylinder);
        this.setApartmentsToIntersection(building);
        showBuildingSwitcher();
        showRightNavBar();
        changeLightEnvironment('cylinder');
        poiToggle(false);
        toggleStreetNames(false);
        await cylinderFloorNumbers();
        rotateCylinderFloorNumbers();

        Amenities.createMainContainer();
        new Amenities(building).add();

        await openSearchHandler();
        setMenuActive('search');
        analytics('page_entrance','search');

        projectMode.Pagination.paginate({source: 'menuHandler', note: 'cylinderMode', menuItem: 'search'});
        
        settingsData.activeModeSwitching = false;
        mobileBurgerStyleToggle();
        userPrevent(false);
    }

    async switchBuilding (name, dataEngineID = false) {
        if (userPrevent()) return;

        userPrevent(true);

        removeSelectionBoxesLockColor();
        Buildings.deactivateBuilding();

        const building = Buildings.getBuilding(name);
        Buildings.activateBuilding(building.name);

        new Amenities(building).add();

        //updateSearchResults();
        changeSelectionBoxesStatusColor(settingsData.searchResult, Buildings.getActiveBuilding());
        //setActiveMultiSearchButton(building.name);

        if (document.getElementById('building-switcher')) {
            // let items_list = document.querySelectorAll('#building-switcher .building-item');
            const activeItem = document.querySelector('#building-switcher .building-item.active');
            const targetItem = document.querySelector(`.building-item[data-building="${building.name}"]`);

            activeItem.classList.remove('active');
            targetItem.classList.add('active');
        }

        const buildingSelectionBoxes = building.selectionsGroup;
        const selectionBox = new THREE.Box3().setFromObject(buildingSelectionBoxes);

        await fitModelInViewport(false, buildingSelectionBoxes);

        this.updateVariables();

        const buildingBoxCenter = selectionBox.getCenter(new Vector3());
        const buildingCenterPoint = buildingSelectionBoxes.getWorldPosition(new Vector3()).setY(buildingBoxCenter.y);

        const {cameraVector, orbitControlsVector} = getWorldData();
        let length;

        if (autoZoomIsActive()) {
            const {foreshortening} = getZoomParams();
            length = foreshortening;
        } else {
            length = (this.maxDistance + this.minDistance) / 2;
        }

        const position = new Vector3()
            .subVectors(cameraVector, buildingCenterPoint)
            .normalize()
            .setLength(length)
            .add(buildingCenterPoint);
        buildingCenterPoint.y = buildingCenterPoint.y / 1.2;

        closeUnitCard();

        if (dataEngineID) {
            userPrevent(false);
            const {selectedMesh} = findSelectionAndCrmData(dataEngineID);
            markSelectedApartments(dataEngineID);
            flatClick(selectedMesh);
            userPrevent(true);
        }

        await animateCamera(
            cameraVector,
            position,
            orbitControlsVector,
            buildingCenterPoint,
            undefined,
            this.minDistance,
            this.minAngle,
            config.threejs.orbitControls.polarCameraCorrection,
            this.switchBuildingDuration,
            TWEEN.Easing.Sinusoidal.InOut,
            () => {
                buildingHoverOut();
                setSmoothDistance();
                resetPanLimits();
                hideCylindersGrid();
                removeCylinderFloorNumbers();
                hideBuildings();
                showBuildings();
            },
            () => {
                showCylinderGrid(building);
            },
        );

        setPolarAngle(ViewMode.Cylinder);
        setMinAndMaxControlsDistance(ViewMode.Cylinder);
        setPanLimits(ViewMode.Cylinder);

        this.setApartmentsToIntersection(building);

        await cylinderFloorNumbers();
        rotateCylinderFloorNumbers();

        userPrevent(false);
        settingsData.activeModeSwitching = false;
    }


    async exitFromMode () {
        if (userPrevent()) return;
        userPrevent(true);
        settingsData.activeModeSwitching = true;

        hideRightNavBar();
        Amenities.hide();
        hideBuildingSwitcher();
        await removeSearchContainer();
        removeSelectionBoxesLockColor();
        closeUnitCard();
        // await searchResizeCanvas();
        await canvasResize();

        Buildings.deactivateBuilding();
        if (!projectMode.LiteMode.isActive()) {
            const buildings = threeJsVariables.scene.getObjectByName('Project Buildings');
            const sBoxes = getHighlights();

            const buildingSize = new THREE.Box3().setFromObject(buildings).getSize(new THREE.Vector3());
            const sBoxSize = new THREE.Box3().setFromObject(sBoxes).getSize(new THREE.Vector3());
            const totalSize = new THREE.Vector3(buildingSize.x, sBoxSize.y, buildingSize.z);

            await fitModelInViewport(false, totalSize);
        } else if (projectMode.LiteMode.isActive() && projectMode.LiteMode.modeIs('picture')) {

            projectMode.LiteMode.moveCameraFromBuilding();
            userPrevent(false);
            settingsData.activeModeSwitching = false;
            settingsData.activeViewMode = 'orbit';
            setMenuActive('3dModel');

            return ;
        }

        const {cameraVector, orbitControlsVector} = getWorldData();
        const target = config.threejs.orbitControls.target;
        const {minAngle} = getPolarAngleFromViewMode(ViewMode.Orbit);
        const {minDistance, maxDistance} = getOrbitControlsDistanceAndPan(ViewMode.Orbit);
        const foreshortening = config.threejs.orbitControls.autoZoomSettings.foreshortening;

        let length;

        if (autoZoomIsActive()) {
            length = foreshortening;
        } else {
            length = (maxDistance + minDistance) / 2;
        }

        const defaultTarget = new Vector3(target.x, target.y, target.z);
        const position = new Vector3().subVectors(cameraVector, orbitControlsVector).normalize().setLength(length).add(orbitControlsVector);

        removeCylinderFloorNumbers();

        settingsData.customSelections = [];
        settingsData.objects_to_intersection = [];
        settingsData.instanced_floors = [];
        settingsData.floor_obj = {};

        await animateCamera(
            cameraVector,
            position,
            orbitControlsVector,
            defaultTarget,
            undefined,
            minDistance,
            minAngle,
            0,
            1000,
            TWEEN.Easing.Sinusoidal.InOut,
            () => {
                setSmoothDistance();
                setOrbitControls(ViewMode.Orbit);
                setPolarAngle(ViewMode.Orbit);
                setPanLimits(ViewMode.Orbit);
                hideTooltip();
            },
            () => {
                setMinAndMaxControlsDistance(ViewMode.Orbit);
                hideTooltip();
            },
        );

        threeJsVariables.scene.remove(this.selectionBoxes);
        this.selectionBoxes = null;
        if (!projectMode.LiteMode.isActive()) showAllBuildings();
        projectMode.LiteMode.isActive() && projectMode.LiteMode.showLiteMode();
        showSceneObjects();
        environmentToggle();

        if (Weather.isActive()) {
            Weather.toggle('enable');
        }

        changeLightEnvironment('orbit');
        updateShadowMap();
        hideCylindersGrid();
        await backgroundShow();
        showBuildingLabels();
        moveElements();
        showHideButtonPlay('show');
        setMenuActive('3dModel');

        settingsData.activeViewMode = 'orbit';
        additionalMenuToggle();
        autoRotateTimerAction('update');
        settingsData.activeModeSwitching = false;
        toggleStreetNames(true);
        poiToggle(true);
        mobileBurgerStyleToggle();
        userPrevent(false);
    }

    updateVariables () {
        this.minDistance = getOrbitControlsDistanceAndPan(ViewMode.Cylinder).minDistance;
        this.maxDistance = getOrbitControlsDistanceAndPan(ViewMode.Cylinder).maxDistance;
    }

    setApartmentsToIntersection (building) {
        settingsData.objects_to_intersection = building.apartments.concat(building.getFloorPlugMeshes());
        settingsData.customSelections = building.customSelections;
        settingsData.objects_to_intersection = settingsData.objects_to_intersection.concat(settingsData.customSelections);
        settingsData.instanced_floors = settingsData.instanced_floors_switch[building.name];
        settingsData.floor_obj = building.selections;

        if (document.querySelector('.search-btn_tooltip')) {
            settingsData.isSwitched = true;
        }
    }

}
